// ======================================================================
// ImsGrid.jsx
// Componente Griglia per Report Vendite
//
// Componente separato che gestisce la visualizzazione della griglia
// per una specifica regione o per tutti i dati
// ======================================================================

import React, { useMemo, useState } from "react";
import {} from "react-bootstrap";
import {
  SortingState,
  IntegratedSorting,
  PagingState,
  IntegratedPaging,
  FilteringState,
  IntegratedFiltering,
  DataTypeProvider,
} from "@devexpress/dx-react-grid";
import {  Card, Collapse, Badge, Button, Row, Col, Dropdown, ButtonGroup, InputGroup, FormControl } from "react-bootstrap";

import { Grid, Table, TableHeaderRow, TableFilterRow, PagingPanel } from "@devexpress/dx-react-grid-bootstrap4";
import "@devexpress/dx-react-grid-bootstrap4/dist/dx-react-grid-bootstrap4.css";
import { calculatePeriodTotals } from "./ImsPeriodUtils";
import { TrendIcon } from "./ImsTrendIcons";

const formatterShort = new Intl.DateTimeFormat("it-IT", { month: "short", year: "2-digit" });

// ======================================================================
// COMPONENTE PER GESTIRE IL FILTRO DELLE PROVINCE PER REGIONE
// ======================================================================
const RegionFilterCell = ({
  regionOptions,
  provOptions,
  skuOptions,
  monthYearOptions,
  regionName,
  provName,
  lineName,
  rows,
  AdvancedFilterCell,
  ...props
}) => {
  // Logica di business: filtra le province per la regione corrente
  const filteredProvOptions = regionName 
    ? provOptions.filter(prov => {
        // Trova se questa provincia esiste nei dati della regione corrente
        return rows.some(row => row.prov === prov.value);
      })
    : provOptions;
  
  return (
    <AdvancedFilterCell
      {...props}
      regionOptions={regionOptions}
      provOptions={filteredProvOptions}
      skuOptions={skuOptions}
      monthYearOptions={monthYearOptions}
    />
  );
};

// ======================================================================
// COMPONENTE: Tabella responsive (wrapper per evitare overflow)
// ======================================================================
const ResponsiveTableComponent = ({ ...restProps }) => {
  // Stile CSS per rimuovere il padding dalle celle
  const tableStyle = `
    .table th, .table td {
      padding: 4 !important;
    }
    .cursor-pointer {
      cursor: pointer !important;
    }
    .cursor-pointer:hover {
      background-color: #f8f9fa !important;
    }
  `;

  return (
    <div className="table-responsive" style={{ overflowX: "auto" }}>
      <style dangerouslySetInnerHTML={{ __html: tableStyle }} />
      <Table.Table
        {...restProps}
        className="table table-hover table-head-custom table-borderless table-striped"
        style={{
          tableLayout: "fixed", // Distribuisce uniformemente le colonne
          width: "100%",
        }}
      />
    </div>
  );
};

// ======================================================================
// COMPONENTE PRINCIPALE GRIGLIA
// ======================================================================
const ImsGrid = ({
  rows,
  columns,
  columnExtensions,
  isMobile,
  CustomHeaderCell,
  RowComponent,
  AdvancedFilterCell,
  regionOptions,
  provOptions,
  skuOptions,
  monthYearOptions,
  ProductTypeProvider,
  TrendTypeProvider,
  DiffTypeProvider,
  PercentTypeProvider,
  NumberTypeProvider,
  CurrencyTypeProvider,
  regionName,
  provName,
  lineName,
}) => {
  // Array di colori per differenziare i periodi
  const periodColors = [
    "#007bff", // Blu
    "#28a745", // Verde
    "#dc3545", // Rosso
    "#ffc107", // Giallo
    "#6f42c1", // Viola
    "#fd7e14", // Arancione
    "#20c997", // Teal
    "#e83e8c", // Rosa
    "#6c757d", // Grigio
    "#17a2b8", // Ciano
  ];
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [isExpanded, setIsExpanded] = useState(false);

  const periodTotals = useMemo(() => calculatePeriodTotals(rows), [rows]);

  if (regionName || provName || lineName) {

    return (
      <Card className="m-4 shadow-sm">
        <Card.Header
          className="bg-light cursor-pointer d-flex justify-content-between align-items-center p-2"
          onClick={() => setIsExpanded(!isExpanded)}
          style={{ cursor: "pointer" }}
        >
          <Col>
            <div className="text-info mb-0 font-weight-bold">
              {/* <i className="fas fa-map-marker-alt me-2 mr-2"></i> */}
              {regionName} {provName} {lineName?.replace(/LINEA/g, "")}
            </div>
            <small className="text-muted">
              {rows.length} {rows.length === 1 ? "prodotto" : "prodotti"}
            </small>
          </Col>
          <Col className="text-right">
            {periodTotals.map(({ periodo, total, trend, fatturatoTotal, fatturatoTrend }, index) => {
              const fattValue =
                fatturatoTotal == null || isNaN(fatturatoTotal)
                  ? null
                  : `€${Number(fatturatoTotal).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

              return (
              <div key={periodo} className={index === 0 ? "mb-1" : "mb-1 mt-1" + " " + (trend === "positive" ? "text-success" : (trend === "negative" ? "text-danger" : undefined))}>
                {periodo.includes(" - ") ? periodo : formatterShort.format(new Date(periodo)).toUpperCase()}:{" "}
                <span className="font-weight-bold"
                  // style={{ color: periodColors[index % periodColors.length] }}
                >
                  {total.toLocaleString()} pz. {fattValue != null ? ` | ${fattValue}` : ""}
                  <TrendIcon trend={trend} />
                </span>
                
              </div>
              );
            })}
          </Col>
          <Col xs={1} className="text-right">
          <i className={`fas fa-chevron-${isExpanded ? "up" : "down"} text-muted`}></i>
          </Col>
        </Card.Header>
        <Collapse in={isExpanded}>
          <Card.Body className="p-0 pt-2">
            <Grid
              rows={rows}
              columns={columns}
              key={columns.length + (isMobile ? "-mobile" : "-desktop") + (regionName || "")}
            >
              <FilteringState defaultFilters={[]} />
              <SortingState defaultSorting={[{ columnName: "date_start", direction: "desc" }]} />
              <PagingState
                currentPage={currentPage}
                onCurrentPageChange={setCurrentPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
              />
              <IntegratedFiltering columnExtensions={columnExtensions} />
              <IntegratedSorting />
              <IntegratedPaging />
              <ProductTypeProvider />
              <TrendTypeProvider />
              <DiffTypeProvider />
              <PercentTypeProvider />
              <NumberTypeProvider />
              <CurrencyTypeProvider />
              <Table
                columnExtensions={columnExtensions}
                tableComponent={ResponsiveTableComponent}
                rowComponent={RowComponent}
              />
              <TableHeaderRow cellComponent={CustomHeaderCell} showSortingControls />
              <TableFilterRow
                cellComponent={(props) => (
                  <RegionFilterCell
                    {...props}
                    regionOptions={regionOptions}
                    provOptions={provOptions}
                    skuOptions={skuOptions}
                    monthYearOptions={monthYearOptions}
                    regionName={regionName}
                    provName={provName}
                    lineName={lineName}
                    rows={rows}
                    AdvancedFilterCell={AdvancedFilterCell}
                  />
                )}
              />
              <PagingPanel
                pageSizes={[10, 20, 50, 100, 0]}
                messages={{
                  info: "Pagina {0} di {1} ({2} elementi)",
                  rowsPerPage: "Righe per pagina:",
                  showAll: "Tutti",
                }}
              />
            </Grid>
          </Card.Body>
        </Collapse>
      </Card>
    );
  }

  return (
    <div>
      <Grid
        rows={rows}
        columns={columns}
        key={columns.length + (isMobile ? "-mobile" : "-desktop") + (regionName || provName || lineName || "")}
      >
        <FilteringState defaultFilters={[]} />
        <SortingState defaultSorting={[{ columnName: "date_start", direction: "desc" }]} />
        <PagingState
          currentPage={currentPage}
          onCurrentPageChange={setCurrentPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
        />
        <IntegratedFiltering columnExtensions={columnExtensions} />
        <IntegratedSorting />
        <IntegratedPaging />
        <ProductTypeProvider />
        <TrendTypeProvider />
        <DiffTypeProvider />
        <PercentTypeProvider />
        <NumberTypeProvider />
        <CurrencyTypeProvider />
        <Table
          columnExtensions={columnExtensions}
          tableComponent={ResponsiveTableComponent}
          rowComponent={RowComponent}
        />
        <TableHeaderRow cellComponent={CustomHeaderCell} showSortingControls />
        <TableFilterRow
          cellComponent={(props) => (
            <AdvancedFilterCell
              {...props}
              regionOptions={regionOptions}
              provOptions={provOptions}
              skuOptions={skuOptions}
              monthYearOptions={monthYearOptions}
            />
          )}
        />
        <PagingPanel
          pageSizes={[10, 20, 50, 100, 0]}
          messages={{
            info: "Pagina {0} di {1} ({2} elementi)",
            rowsPerPage: "Righe per pagina:",
            showAll: "Tutti",
          }}
        />
      </Grid>
    </div>
  );
};

export default ImsGrid;
